import ReactEcharts from 'echarts-for-react';
import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
  ChartControlsProps,
  ChartControlStateProps,
} from '../../redux/ChartPoperties/ChartControlsInterface';
import { ColorSchemes } from '../ChartOptions/Color/ColorScheme';
import {
  formatChartLabelValue,
  formatChartLabelValueForSelectedMeasure,
} from '../ChartOptions/Format/NumberFormatter';
import {
  ChartsMapStateToProps,
  ChartsReduxStateProps,
  FormatterValueProps,
} from './ChartsCommonInterfaces';

import { palette } from '../..';

import {
  checkUidPresenceInCheckedMeasureForTooltip,
  shouldChartDisplayWithSecurity,
} from '../CommonFunctions/CommonFunctions';
import EmptyQuery from './EmptyQuery';
import { useSelector } from 'react-redux';
import { RootState } from '../../redux';
import { tooltipStyle } from '../Common/EChartTooltip';
import useCombinedColorSchemes from '../../hooks/useCombinedColorSchemes';
import {
  LABEL_THRESHOLDS,
  LEGEND_THRESHOLDS,
  MARGIN_THRESHOLDS,
} from '../ChartOptions/ChartThresholds';

const FunnelChart = ({
  //props
  propKey,
  graphDimension,
  chartArea,
  graphTileSize,
  fontFamily,
  colorScheme,
  softUI,

  //state
  chartControls,
  chartProperties,
}: ChartsReduxStateProps) => {
  var chartControl: ChartControlsProps = chartControls.properties[propKey];
  let chartData: any[] = chartControl.chartData ? chartControl.chartData : [];
  const currentControls = chartControls.properties[propKey].funnelChartControls || {};
  const { manualColor, nodeValuesColors, allSeriesColor } = currentControls;
  const { tooltipFormats, chartAxes, isTooltipEnabled, chartType } = useSelector(
    (state: RootState) => ({
      tooltipFormats: state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats,
      chartAxes: state.chartProperties.properties[propKey]?.chartAxes,
      isTooltipEnabled:
        state.chartControls?.properties[propKey]?.formatOptions?.tooltipFormats?.isEnable,
      chartType: state.chartProperties?.properties[propKey]?.chartType,
    }),
  );

  const [funnelChartData, setFunnelChartData] = useState<any[]>([]);
  const [currentLegendSelected, setCurrentLegendSelected] = useState<any>();
  const allThemes = useCombinedColorSchemes();
  const chartThemes = allThemes.filter((el) => {
    if (!chartControl.colorScheme || chartArea === 'dashboard') {
      return el.name === colorScheme;
    } else {
      return el.name === chartControl.colorScheme;
    }
  });
  const themeColors = chartThemes[0]?.colors || ColorSchemes[0].colors;
  useEffect(() => {
    if (chartData.length >= 1) {
      var newFunnelChartData: any[] = [];
      const keys = Object.keys(chartData[0]);

      keys.forEach((key: string, index: number) => {
        let sliceColor = themeColors[index % themeColors.length];

        if (manualColor) {
          if (nodeValuesColors && nodeValuesColors[key]) {
            sliceColor = nodeValuesColors[key];
          } else if (
            (key === 'null' || key === null) &&
            nodeValuesColors &&
            nodeValuesColors['null']
          ) {
            sliceColor = nodeValuesColors['null'];
          } else if (allSeriesColor) {
            sliceColor = allSeriesColor;
          }
        }

        newFunnelChartData.push({
          name: key,
          value: chartData[0][key],
          itemStyle: {
            color: sliceColor,
            ...(softUI
              ? {
                  shadowBlur: 10,
                  shadowColor: 'rgba(0, 0, 0, 0.5)',
                  shadowOffsetX: 5,
                  shadowOffsetY: 5,
                }
              : {}),
          },
        });
      });
      setFunnelChartData(newFunnelChartData);
    }
  }, [chartData, manualColor, nodeValuesColors, allSeriesColor, themeColors, softUI]);

  const onLegendSelectChanged = (params: any) => {
    setCurrentLegendSelected(params.selected);
  };

  const RenderChart = () => {
    return (
      <ReactEcharts
        onEvents={{
          legendselectchanged: onLegendSelectChanged,
        }}
        // theme={chartControl.colorScheme}
        style={{
          // padding: '1rem',
          width: graphDimension.width,
          height: graphDimension.height,
          overflow: 'hidden',
          margin: 'auto',
          border: chartArea ? 'none' : graphTileSize ? 'none' : '1px solid rgb(238,238,238)',
        }}
        option={{
          color: chartThemes[0]?.colors || [],
          backgroundColor: chartThemes[0]?.background || [],
          // animation: chartArea ? false : true, // Turning animation off for current release, to be added later on
          animation: false,
          legend: {
            // textStyle :{color : getContrastColor(chartThemes[0].background)},
            type: 'scroll',
            show:
              Math.floor(graphDimension.height) > LEGEND_THRESHOLDS.funnel.h
                ? chartControl.legendOptions?.showLegend
                : false,
            selected: currentLegendSelected,
            itemHeight:
              chartArea === 'dashboard'
                ? chartControl.legendOptions?.symbolHeight / 2
                : chartControl.legendOptions?.symbolHeight,
            itemWidth:
              chartArea === 'dashboard'
                ? chartControl.legendOptions?.symbolWidth / 2
                : chartControl.legendOptions?.symbolWidth,
            itemGap: chartControl.legendOptions?.itemGap,

            left: chartControl.legendOptions?.position?.left,
            top: chartControl.legendOptions?.position?.top,
            orient: chartControl.legendOptions?.orientation,
            textStyle: {
              color: chartThemes[0]?.dark ? '#ffffff' : palette.primary.contrastText,
              fontFamily: fontFamily,
            },
          },

          tooltip: {
            show: isTooltipEnabled,
            useHTML: true,
            trigger: 'item',
            backgroundColor: 'white',
            borderWidth: 1,
            padding: 0,
            textStyle: {
              color: '#333',
              fontSize: 16,
            },
            appendToBody: true,
            extraCssText:
              'box-shadow: 0 2px 8px rgba(0,0,0,0.15);white-space: normal; word-break: break-word;',
            formatter: function (params: any) {
              const hoveredFieldname = params.data.name;
              //---------get list of non hovered field(s) -------//
              const nonHoveredFieldList = funnelChartData
                .map((item: any) => item.name)
                .filter((name: string) => name !== hoveredFieldname);
              const tempData: { [key: string]: string | number } = {
                [params.data.name]: params.data.value,
              };
              const _data: { [key: string]: string | number } = {};
              //----------remove unchecked measure or any other field value of type date inside tooltip----------//
              Object.keys(tempData).forEach((key: string) => {
                const resp = checkUidPresenceInCheckedMeasureForTooltip(
                  chartAxes,
                  key,
                  tooltipFormats?.checkedMeasure,
                );
                if (resp) {
                  _data[key] = tempData[key];
                }
              });
              let mergedData = { ..._data };
              //-- send extra responses in _data so that it maps and shows all key value pairs including extra measure (if any)--//
              //----get extra response corresponding to a particular dimension----//
              const extraResponses = tooltipFormats?.extraMeasureFieldsResponses[0];
              if (tooltipFormats && extraResponses) {
                const isPresent = checkUidPresenceInCheckedMeasureForTooltip(
                  chartAxes,
                  hoveredFieldname,
                  tooltipFormats?.checkedMeasure,
                );
                let filteredFields = { ...extraResponses };
                //------ delete nonHoveredFieldList's name (show dimension, hovered field & extra field) -----//
                for (let key in extraResponses) {
                  if (nonHoveredFieldList.includes(key)) {
                    delete filteredFields[key];
                    //---if there is hovered field in unchecked then don't show---//
                    if (!isPresent) {
                      delete filteredFields[hoveredFieldname];
                    }
                  }
                }
                mergedData = { ...mergedData, ...filteredFields };
              }
              return tooltipStyle(
                params.color,
                mergedData,
                tooltipFormats,
                chartAxes,
                undefined,
                undefined,
                undefined,
                fontFamily,
              );
            },
          },
          // dataset: {
          //   source: funnelChartData,
          // },

          series: [
            {
              type: 'funnel',
              label: {
                show:
                  Math.floor(graphDimension.height) > LABEL_THRESHOLDS.funnel.h &&
                  Math.floor(graphDimension.width) > LABEL_THRESHOLDS.funnel.w
                    ? chartControl.labelOptions.showLabel
                    : false,
                fontSize: chartControl.labelOptions.fontSize,
                fontFamily: fontFamily,
                color: chartControl.labelOptions.labelColor
                  ? chartControl.labelOptions.labelColor
                  : chartThemes[0]?.dark
                    ? '#ffffff'
                    : '#757474',
                backgroundColor: chartControl.labelOptions.labelBackground
                  ? chartControl.labelOptions.labelBackgroundColor
                  : 'transparent',
                borderRadius: chartControl.labelOptions.labelBackground ? 4 : 0,
                padding: chartControl.labelOptions.labelBackground ? [4, 8] : 0,
                formatter: (value: FormatterValueProps) => {
                  var formattedValue = value.value.value;
                  formattedValue = formatChartLabelValueForSelectedMeasure(
                    chartControls.properties[propKey],
                    chartProperties.properties[propKey],
                    formattedValue,
                    value.value.name,
                  );
                  return formattedValue;
                },
              },
              emphasis: {
                label: {
                  show:
                    Math.floor(graphDimension.height) > LABEL_THRESHOLDS.funnel.h &&
                    Math.floor(graphDimension.width) > LABEL_THRESHOLDS.funnel.w
                      ? chartControl.labelOptions.showLabel
                      : false,
                },
              },
              top:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? '0%'
                  : chartControl.legendOptions?.position?.top === 'top'
                    ? chartControl.chartMargin.top + 5 + '%'
                    : chartControl.chartMargin.top + '%',
              bottom:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? '0%'
                  : chartControl.legendOptions?.position?.top === 'bottom'
                    ? (graphDimension.height * chartControl.chartMargin.bottom) / 100 + 20
                    : chartControl.chartMargin.bottom + '%',
              left:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? '0%'
                  : chartControl.chartMargin.funnelLeft + '%',
              right:
                Math.floor(graphDimension.height) <= MARGIN_THRESHOLDS.compact.h
                  ? '0%'
                  : chartControl.chartMargin.funnelRight + '%',
              data: funnelChartData,
            },
          ],
        }}
      />
    );
  };

  const emptyQueryCondition =
    shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) === false ||
    chartData.length < 1;

  return emptyQueryCondition ? (
    <EmptyQuery
      graphDimension={graphDimension}
      propKey={propKey}
      chartArea={chartArea}
      graphTileSize={graphTileSize}
      chartThemes={chartThemes}
      showEmptyQueryMessageWithSecurity={
        !shouldChartDisplayWithSecurity(chartAxes, chartType, chartData) ? true : false
      }
    />
  ) : (
    <RenderChart />
  );
};
const mapStateToProps = (state: ChartsMapStateToProps, ownProps: any) => {
  return {
    chartControls: state.chartControls,
    chartProperties: state.chartProperties,
  };
};

export default connect(mapStateToProps, null)(FunnelChart);
